import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from "react";
import {
  USE_DEMO_DATA,
  authApi,
  type LoginInput,
  type Session,
  type SignUpInput
} from "./lib/api";
import { logoutWebPush } from "./lib/onesignal";

type AuthContextValue = {
  session: Session | null;
  loading: boolean;
  login: (input: LoginInput) => Promise<void>;
  signup: (input: SignUpInput) => Promise<string>;
  logout: () => Promise<void>;
  forgotPassword: (email: string, turnstileToken: string) => Promise<string>;
};

const DEMO_SESSION: Session = {
  user: {
    id: "30000000-0000-4000-8000-000000000001",
    email: "demo@solab.quocminh.org",
    displayName: "Quốc Minh",
    adultAcknowledged: true
  }
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        if (USE_DEMO_DATA) {
          const hasDemoSession = window.localStorage.getItem("lottolab-demo-session") === "active";
          if (active) setSession(hasDemoSession ? DEMO_SESSION : null);
        } else {
          const next = await authApi.session();
          if (active) setSession(next);
        }
      } catch {
        if (active) setSession(null);
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(async (input: LoginInput) => {
    if (USE_DEMO_DATA) {
      window.localStorage.setItem("lottolab-demo-session", "active");
      setSession({ ...DEMO_SESSION, user: { ...DEMO_SESSION.user, email: input.email } });
      return;
    }
    setSession(await authApi.login(input));
  }, []);

  const signup = useCallback(async (input: SignUpInput) => {
    if (USE_DEMO_DATA) {
      window.localStorage.setItem("lottolab-demo-session", "active");
      setSession({
        user: {
          ...DEMO_SESSION.user,
          email: input.email,
          displayName: input.displayName || null
        }
      });
      return "Đã tạo tài khoản demo.";
    }
    const result = await authApi.signup(input);
    return result.message;
  }, []);

  const logout = useCallback(async () => {
    logoutWebPush();
    if (USE_DEMO_DATA) {
      window.localStorage.removeItem("lottolab-demo-session");
    } else {
      await authApi.logout();
    }
    setSession(null);
  }, []);

  const forgotPassword = useCallback(async (email: string, turnstileToken: string) => {
    if (USE_DEMO_DATA) {
      return `Đã mô phỏng gửi email khôi phục tới ${email}.`;
    }
    const result = await authApi.forgotPassword(email, turnstileToken);
    return result.message;
  }, []);

  const value = useMemo(
    () => ({ session, loading, login, signup, logout, forgotPassword }),
    [forgotPassword, loading, login, logout, session, signup]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth phải được dùng trong AuthProvider.");
  return value;
}
